import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, Eye } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { hr } from 'date-fns/locale';
import { useLanguage } from '../context/LanguageContext';

// Inline SVG fallback (data URI) — TimesCroatia red mark, used when an image
// fails to load (e.g. broken hotlink, DNS error, image_credit "Google AI Labs"
// placeholder). Kept tiny so it never adds network weight.
const FALLBACK_SVG = (
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 320 200'>
      <defs><linearGradient id='g' x1='0' y1='0' x2='0' y2='1'>
        <stop offset='0' stop-color='#b41c1f'/><stop offset='1' stop-color='#6e1014'/>
      </linearGradient></defs>
      <rect width='320' height='200' fill='url(#g)'/>
      <text x='50%' y='52%' fill='#fff5ef' font-family='Georgia, serif' font-weight='800' font-size='30' text-anchor='middle'>TimesCroatia</text>
      <text x='50%' y='72%' fill='#f7d3c8' font-family='Inter, sans-serif' font-size='12' text-anchor='middle'>zanimljivosti iz Hrvatske i šire</text>
    </svg>`
  )
);

const handleImgError = (e) => {
  if (e.currentTarget.dataset.fallback === '1') return;
  e.currentTarget.dataset.fallback = '1';
  e.currentTarget.src = FALLBACK_SVG;
};

const Img = (props) => (
  <img
    loading="lazy"
    decoding="async"
    {...props}
    referrerPolicy="no-referrer"
    onError={handleImgError}
  />
);

export default function ArticleCard({ article, size = 'medium', horizontal = false }) {
  const { t } = useLanguage();
  if (!article) return null;

  const timeAgo = article.published_at
    ? formatDistanceToNow(new Date(article.published_at), { addSuffix: true, locale: hr })
    : '';

  const wordCount = (article.content || article.excerpt || '').split(/\s+/).length;
  const readingTime = Math.max(1, Math.ceil(wordCount / 200));

  if (horizontal) {
    return (
      <article className="article-card flex gap-3 group" data-testid="article-card">
        <Link to={`/article/${article.slug}`} className="flex-shrink-0">
          <div className="w-24 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-white/5">
            <Img src={article.featured_image || FALLBACK_SVG} alt={article.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          </div>
        </Link>
        <div className="flex-1 min-w-0">
          {article.category && (
            <Link to={`/category/${article.category.toLowerCase()}`} className="category-badge text-sky-accent">{article.category}</Link>
          )}
          <Link to={`/article/${article.slug}`}>
            <h3 className="font-display text-sm font-semibold text-navy dark:text-white leading-snug mt-0.5 line-clamp-2 group-hover:text-sky-accent transition-colors">{article.title}</h3>
          </Link>
          <p className="text-[10px] text-gray-400 mt-0.5 font-display">{timeAgo}</p>
        </div>
      </article>
    );
  }

  if (size === 'large') {
    return (
      <article className="article-card group relative overflow-hidden rounded-2xl bg-white dark:bg-navy-light border border-gray-100 dark:border-white/5" data-testid="article-card-large">
        <Link to={`/article/${article.slug}`}>
          <div className="aspect-video overflow-hidden">
            <Img src={article.featured_image || FALLBACK_SVG} alt={article.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          </div>
        </Link>
        <div className="p-5">
          {article.category && (
            <Link to={`/category/${article.category.toLowerCase()}`} className="category-badge text-sky-accent hover:text-sky-accent-dark transition-colors">{article.category}</Link>
          )}
          <Link to={`/article/${article.slug}`}>
            <h2 className="font-serif text-xl font-bold text-navy dark:text-white mt-2 mb-2 leading-tight group-hover:text-sky-accent transition-colors line-clamp-3">{article.title}</h2>
          </Link>
          {article.excerpt && (
            <p className="text-gray-500 dark:text-gray-400 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
          )}
          <div className="flex items-center justify-between text-xs text-gray-400 font-display">
            <div className="flex items-center gap-1"><Clock size={11} /> {timeAgo}</div>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1"><Eye size={11} /> {readingTime} min</span>
              {article.view_count > 0 && <span>{article.view_count} views</span>}
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="article-card group bg-white dark:bg-navy-light rounded-2xl overflow-hidden border border-gray-100 dark:border-white/5" data-testid="article-card-medium">
      <Link to={`/article/${article.slug}`}>
        <div className="aspect-[16/10] overflow-hidden">
          <Img src={article.featured_image || FALLBACK_SVG} alt={article.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        </div>
      </Link>
      <div className="p-4">
        {article.category && (
          <Link to={`/category/${article.category.toLowerCase()}`} className="category-badge text-sky-accent hover:text-sky-accent-dark">{article.category}</Link>
        )}
        <Link to={`/article/${article.slug}`}>
          <h3 className="font-serif font-bold text-navy dark:text-white text-base mt-1.5 mb-2 leading-tight group-hover:text-sky-accent transition-colors line-clamp-2">{article.title}</h3>
        </Link>
        <div className="flex items-center justify-between text-[11px] text-gray-400 font-display">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><Clock size={10} /> {timeAgo}</span>
            <span className="flex items-center gap-1"><Eye size={10} /> {readingTime} min</span>
          </div>
          {article.author && <span className="truncate max-w-[100px]">{article.author}</span>}
        </div>
      </div>
    </article>
  );
}
